import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, User, Send, Shield, Lock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';

const FacebookIcon = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>;
const TwitterIcon = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5 2.8 11 2.8 11c.5.3 1.2.3 1.8.2C2 9.5 2 5 2 5c.5.3 1.2.5 1.8.5C1.5 3 3.5 1 5.5 3c2.5 3.5 6 5.5 10.5 5.5.5-3.5 4-5.5 6-3.5z" /></svg>;
const InstagramIcon = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" x2="17.51" y1="6.5" y2="6.5" /></svg>;
const LinkedinIcon = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect width="4" height="12" x="2" y="9" /><circle cx="4" cy="4" r="2" /></svg>;

// ============================================
// GOOGLE SHEETS CONFIGURATION
// ============================================
// backup.maksoftweb@gmail.com ki Google Sheet
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyLY9-n_LVeOy1Y50cKEq5rEbILDHY0yKAvAXrcB3y4hRd-hHAqNNVLxSyu4DO68fuM/exec';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    subject: '',
    message: '',
    source: ''
  });

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  const [rateLimit, setRateLimit] = useState({
    isLimited: false,
    countdown: null,
    resetTime: null
  });

  useEffect(() => {
    checkRateLimit();
  }, []);

  useEffect(() => {
    let interval;
    if (rateLimit.isLimited && rateLimit.resetTime) {
      interval = setInterval(() => {
        const now = Date.now();
        const reset = rateLimit.resetTime;
        const diff = Math.max(0, Math.floor((reset - now) / 1000));

        if (diff <= 0) {
          setRateLimit(prev => ({ ...prev, isLimited: false, countdown: null }));
          localStorage.removeItem('maksoft_contact_last_submit');
          clearInterval(interval);
        } else {
          const minutes = Math.floor(diff / 60);
          const seconds = diff % 60;
          setRateLimit(prev => ({ ...prev, countdown: `${minutes}:${seconds.toString().padStart(2, '0')}` }));
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [rateLimit.isLimited, rateLimit.resetTime]);

  const checkRateLimit = () => {
    const lastSubmit = localStorage.getItem('maksoft_contact_last_submit');
    const RATE_LIMIT_MINUTES = 2;
    
    if (lastSubmit) {
      const lastTime = parseInt(lastSubmit);
      const now = Date.now();
      const diff = now - lastTime;
      const limitMs = RATE_LIMIT_MINUTES * 60 * 1000;
      
      if (diff < limitMs) {
        const resetTime = lastTime + limitMs;
        const remainingSec = Math.floor((resetTime - now) / 1000);
        const minutes = Math.floor(remainingSec / 60);
        const seconds = remainingSec % 60;
        
        setRateLimit({
          isLimited: true,
          countdown: `${minutes}:${seconds.toString().padStart(2, '0')}`,
          remaining: 0,
          resetTime: resetTime
        });
      }
    }
  };

  const handleChange = (e) => {
    const { id, name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id || name]: value
    }));
    if (status.error) setStatus(prev => ({ ...prev, error: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rateLimit.isLimited) {
      setStatus({
        loading: false,
        success: false,
        error: `Please wait ${rateLimit.countdown || 'a few minutes'} before submitting again.`
      });
      return;
    }

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setStatus({ loading: false, success: false, error: 'Name, email, and message are required' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setStatus({ loading: false, success: false, error: 'Please enter a valid email address' });
      return;
    }

    setStatus({ loading: true, success: false, error: null });

    try {
      const params = new URLSearchParams();
      params.append('name', formData.name.trim());
      params.append('email', formData.email.trim());
      params.append('phone', formData.phone.trim());
      params.append('projectType', formData.projectType || 'Not Selected');
      params.append('subject', formData.subject || formData.projectType || 'New Contact');
      params.append('message', formData.message.trim());
      params.append('source', formData.source || 'Not Selected');

      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString()
      });
      
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const now = Date.now();
      const resetTime = now + (2 * 60 * 1000);
      localStorage.setItem('maksoft_contact_last_submit', now.toString());
      
      setRateLimit({
        remaining: 0,
        resetTime: resetTime,
        isLimited: true,
        countdown: '2:00'
      });
      
      setStatus({ loading: false, success: true, error: null });
      
      setFormData({ 
        name: '', 
        email: '', 
        phone: '', 
        projectType: '', 
        subject: '', 
        message: '',
        source: ''
      });

    } catch (err) {
      console.error('Submission error:', err);
      setStatus({
        loading: false,
        success: false,
        error: 'Failed to submit. Please try again later.'
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628] text-white">
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative pt-40 pb-24 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-[url('/assest/logo.jfif')] bg-cover bg-center bg-no-repeat">
            <div className="absolute inset-0 bg-[#0a1628]/75"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto flex flex-col items-center justify-center text-center w-full">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-[#046A8C]/20 rounded-full px-4 py-1 mb-8"
            >
              <Mail className="w-4 h-4 text-[#EFCD86]" />
              <span className="text-[#EFCD86] text-xs uppercase tracking-widest font-semibold">Get In Touch</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-5xl md:text-6xl font-bold leading-tight"
            >
              <span className="block text-white">Let&apos;s <span className="text-[#EFCD86]">talk</span> business.</span>
              <span className="block text-white">But you first.</span>
            </motion.h1>
          </div>
        </section>

        {/* Main Content */}
        <section className="px-4 py-32 relative">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

            {/* LEFT - Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#0d1f33]/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8 lg:p-10 shadow-2xl relative"
            >
              <div className="w-12 h-1 bg-[#EFCD86] mb-6 rounded-full"></div>
              <h3 className="text-2xl font-bold mb-8 text-white">Send us a message</h3>

              {/* SUCCESS MESSAGE */}
              {status.success && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-green-500/20 border border-green-500/30 rounded-lg flex items-center gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
                  <div>
                    <p className="text-green-400 font-medium">Thank you!</p>
                    <p className="text-green-300/80 text-sm">We will contact you soon.</p>
                  </div>
                </motion.div>
              )}

              {/* RATE LIMIT WARNING */}
              {rateLimit.isLimited && rateLimit.countdown && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-orange-500/20 border border-orange-500/30 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-orange-400 shrink-0 animate-pulse" />
                    <div>
                      <p className="text-orange-400 font-medium">Rate Limit Active</p>
                      <p className="text-orange-300/80 text-sm">
                        Please wait <span className="font-bold text-orange-400">{rateLimit.countdown}</span> before submitting again.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ERROR MESSAGE */}
              {status.error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-3"
                >
                  <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
                  <p className="text-red-400 text-sm">{status.error}</p>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                {/* Name */}
                <div className="relative group">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-[#EFCD86] transition-colors" />
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder=" "
                    required
                    className="peer w-full bg-[#0a1628] border border-white/10 rounded-lg text-white focus:border-[#EFCD86] focus:ring-2 focus:ring-[#EFCD86]/20 py-3 pr-3 pl-10 outline-none transition-all placeholder-transparent"
                  />
                  <label htmlFor="name" className="absolute left-10 -top-2.5 text-sm text-[#EFCD86] bg-[#0d1f33] px-2 transition-all cursor-text peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-[#EFCD86] rounded">
                    Hello, my Name is
                  </label>
                </div>

                {/* Email */}
                <div className="relative group">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-[#EFCD86] transition-colors" />
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder=" "
                    required
                    className="peer w-full bg-[#0a1628] border border-white/10 rounded-lg text-white focus:border-[#EFCD86] focus:ring-2 focus:ring-[#EFCD86]/20 py-3 pr-3 pl-10 outline-none transition-all placeholder-transparent"
                  />
                  <label htmlFor="email" className="absolute left-10 -top-2.5 text-sm text-[#EFCD86] bg-[#0d1f33] px-2 transition-all cursor-text peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-[#EFCD86] rounded">
                    Here is my email
                  </label>
                </div>

                {/* Phone */}
                <div className="relative group">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-[#EFCD86] transition-colors" />
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder=" "
                    className="peer w-full bg-[#0a1628] border border-white/10 rounded-lg text-white focus:border-[#EFCD86] focus:ring-2 focus:ring-[#EFCD86]/20 py-3 pr-3 pl-10 outline-none transition-all placeholder-transparent"
                  />
                  <label htmlFor="phone" className="absolute left-10 -top-2.5 text-sm text-[#EFCD86] bg-[#0d1f33] px-2 transition-all cursor-text peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-[#EFCD86] rounded">
                    My phone (optional)
                  </label>
                </div>

                {/* Project Type & Source */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
                  <div className="relative">
                    <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">Project Type</label>
                    <select
                      id="projectType"
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      className="w-full bg-[#0a1628] border border-white/10 text-gray-300 rounded-lg p-3 outline-none focus:border-[#EFCD86] focus:ring-2 focus:ring-[#EFCD86]/20 transition-all appearance-none cursor-pointer"
                    >
                      <option value="">Select an option</option>
                      <option>Web Development</option>
                      <option>Mobile App</option>
                      <option>UI/UX Design</option>
                      <option>IT Consultation</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="relative">
                    <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">How did you hear?</label>
                    <select
                      id="source"
                      name="source"
                      value={formData.source}
                      onChange={handleChange}
                      className="w-full bg-[#0a1628] border border-white/10 text-gray-300 rounded-lg p-3 outline-none focus:border-[#EFCD86] focus:ring-2 focus:ring-[#EFCD86]/20 transition-all appearance-none cursor-pointer"
                    >
                      <option value="">Select an option</option>
                      <option>Google Search</option>
                      <option>Social Media</option>
                      <option>Referral</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div className="pt-2">
                  <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">About project</label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    required
                    placeholder="Your project description..."
                    className="w-full bg-[#0a1628] border border-white/10 rounded-lg text-white focus:border-[#EFCD86] focus:ring-2 focus:ring-[#EFCD86]/20 p-3 outline-none transition-all resize-none"
                  ></textarea>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={status.loading || rateLimit.isLimited}
                  className={`w-full relative group font-semibold py-4 rounded-lg transition-all duration-300 overflow-hidden
                    ${rateLimit.isLimited
                      ? 'bg-gray-600 cursor-not-allowed opacity-60'
                      : 'bg-gradient-to-r from-[#046A8C] to-[#67B253] hover:shadow-lg hover:shadow-[#046A8C]/30'
                    }`}
                >
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                  <div className="relative flex items-center justify-center gap-2 text-white">
                    {status.loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : rateLimit.isLimited ? (
                      <>
                        <Clock className="w-5 h-5" />
                        Wait {rateLimit.countdown || '...'}
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </div>
                </button>

                {/* Info */}
                <div className="flex items-center justify-center gap-2 text-gray-400 text-sm mt-4">
                  <Clock className="w-4 h-4 text-[#EFCD86]" />
                  We typically respond within 24 hours
                </div>

                {/* Trust Badges */}
                <div className="pt-6 mt-6 border-t border-white/5 flex flex-wrap items-center justify-center gap-6 text-gray-500 text-xs tracking-wide">
                  <div className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Secure Form</div>
                  <div className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> SSL Encrypted</div>
                  <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> 2 min Limit</div>
                  <div className="flex items-center gap-1.5">No Spam</div>
                </div>
              </form>
            </motion.div>

            {/* RIGHT - Contact Info Cards */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col justify-center space-y-6"
            >
              <div className="w-12 h-1 bg-[#EFCD86] mb-2 rounded-full"></div>
              <h3 className="text-3xl font-bold text-white mb-2">Contact Information</h3>
              <p className="text-gray-400 mb-6 leading-relaxed">Reach out to us directly through any of these channels. Our team is ready to assist you.</p>

              <a href="mailto:sales@maksoft.com" className="group bg-[#0d1f33]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6 flex items-center gap-6 hover:-translate-y-1 hover:border-[#EFCD86]/30 hover:shadow-xl hover:shadow-[#EFCD86]/5 transition-all duration-300">
                <div className="bg-[#EFCD86]/10 rounded-full w-14 h-14 flex items-center justify-center shrink-0 group-hover:bg-[#EFCD86] transition-colors">
                  <Mail className="text-[#EFCD86] w-6 h-6 group-hover:text-[#0d1f33] transition-colors" />
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Email Us</h4>
                  <p className="text-gray-400 group-hover:text-gray-300 transition-colors">sales@maksoft.com</p>
                </div>
              </a>

              <a href="tel:+923002086828" className="group bg-[#0d1f33]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6 flex items-center gap-6 hover:-translate-y-1 hover:border-[#EFCD86]/30 hover:shadow-xl hover:shadow-[#EFCD86]/5 transition-all duration-300">
                <div className="bg-[#EFCD86]/10 rounded-full w-14 h-14 flex items-center justify-center shrink-0 group-hover:bg-[#EFCD86] transition-colors">
                  <Phone className="text-[#EFCD86] w-6 h-6 group-hover:text-[#0d1f33] transition-colors" />
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Call Us</h4>
                  <p className="text-gray-400 group-hover:text-gray-300 transition-colors">+92 300 2086828</p>
                </div>
              </a>

              <div className="group bg-[#0d1f33]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6 flex items-center gap-6 hover:-translate-y-1 hover:border-[#EFCD86]/30 hover:shadow-xl hover:shadow-[#EFCD86]/5 transition-all duration-300 cursor-default">
                <div className="bg-[#EFCD86]/10 rounded-full w-14 h-14 flex items-center justify-center shrink-0 group-hover:bg-[#EFCD86] transition-colors">
                  <Clock className="text-[#EFCD86] w-6 h-6 group-hover:text-[#0d1f33] transition-colors" />
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Working Hours</h4>
                  <p className="text-gray-400 group-hover:text-gray-300 transition-colors">Mon - Sat, 10am to 8pm</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-32 px-4 relative max-w-7xl mx-auto w-full">
          <div className="mb-12 text-center flex flex-col items-center">
            <div className="w-12 h-1 bg-[#EFCD86] mb-4 rounded-full"></div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-[#EFCD86] text-4xl font-bold uppercase tracking-wide"
            >
              Visit Our Office
            </motion.h2>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="w-full h-96 relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl group"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3619.403967308125!2d67.06127897520084!3d24.884198677913844!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3eb33f0058a04baf%3A0xdf50f2835f78bbee!2sZulekha%20Trade%20Centre!5e0!3m2!1sen!2s!4v1776532398622!5m2!1sen!2s"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="MakSoft Location"
              className="grayscale group-hover:grayscale-0 transition-all duration-700"
            ></iframe>
            <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 bg-[#0d1f33]/90 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl max-w-sm transform transition-transform duration-500 hover:-translate-y-2">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-[#EFCD86]/20 p-2 rounded-full"><MapPin className="text-[#EFCD86] w-5 h-5" /></div>
                <h4 className="text-white font-bold text-lg">We&apos;re here</h4>
              </div>
              <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                Office # 509, 5th Floor, Zulekha Trade Centre, Bahadurabad, Karachi, Pakistan
              </p>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=Zulekha+Trade+Centre,Karachi"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-[#EFCD86] text-[#0a1628] font-semibold rounded-lg px-6 py-2.5 hover:bg-white transition-colors w-full text-center"
              >
                Get Directions
              </a>
            </div>
          </motion.div>
        </section>

        {/* Footer CTA */}
        <section className="w-full py-32 relative text-center overflow-hidden">
          <div className="absolute inset-0 bg-[url('/assest/logo.jfif')] bg-cover bg-center bg-no-repeat bg-fixed">
            <div className="absolute inset-0 bg-[#0a1628]/90 backdrop-blur-sm"></div>
          </div>
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#EFCD86]/50 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#EFCD86]/50 to-transparent"></div>
          <div className="relative z-10 max-w-4xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-[#EFCD86] uppercase tracking-widest text-xs font-bold mb-6">HELLO! WE&apos;RE LISTENING</p>
              <h2 className="flex flex-col gap-2 mb-12">
                <span className="text-5xl md:text-7xl font-bold text-white">Let&apos;s talk</span>
                <span className="text-6xl md:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#046A8C] to-[#67B253]">about your project</span>
              </h2>
              <div className="pt-4">
                <span className="inline-block text-[#EFCD86] font-medium tracking-widest hover:underline underline-offset-8 transition-all duration-300 cursor-pointer">
                  SOUND GOOD? LET&apos;S CONNECT &rarr;
                </span>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactUs;